import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardText, Spinner } from 'reactstrap';
import RoleDescriptionSectionCard from './RoleDescriptionSectionCard';

const roleDocMapById = {
  founding50: 'Founding-50-Role-Description.md',
  dayOnes: 'Day-Ones-Role-Description.md',
  influencers: 'Influencers-Content-Creators-Role-Description.md',
  leagueHosts: 'League-Hosts-Role-Description.md'
};

const toKebabCase = text =>
  String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

function RoleMarkdownRenderer({ scrollableRef, isUserAuthenticated = false, isApplyDisabled = true, isRoleCapped, submissionStatus, acceptedAcknowledgements = [], role = {}, descriptionData = {}, onSectionsRender, scrollToSection, onApply, toggleAcknowledgement, showConfirmModal }) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [markdownContent, setMarkdownContent] = useState('');

  const roleDocFilename = useMemo(() => {
    if (role?.slug) return `${toKebabCase(role.slug)}.md`;
    if (role?.id && roleDocMapById[role.id]) return roleDocMapById[role.id];
    return `${toKebabCase(role?.title)}.md`;
  }, [role]);

  // Scroll to top when role changes
  useEffect(() => {
    if (scrollableRef.current) scrollableRef.current.scrollTop = 0;
  }, [roleDocFilename, scrollableRef]);

  useEffect(() => {
    let isMounted = true;

    const loadRoleDescription = async () => {
      if (!roleDocFilename) return;

      setIsLoading(true);
      setErrorMessage('');
      setMarkdownContent('');

      try {
        const response = await fetch(`/role_descriptions/${roleDocFilename}`);
        if (!response.ok) throw new Error(`Unable to load role description: ${response.status}`);

        const markdownText = await response.text();
        if (isMounted) setMarkdownContent(markdownText);
      } catch {
        if (isMounted) {
          setErrorMessage('Role description unavailable for this selection right now. Please try another role or apply to continue.');
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadRoleDescription();

    return () => {
      isMounted = false;
    };
  }, [roleDocFilename]);

  // Notify parent when sections are rendered
  useEffect(() => {
    if (!scrollableRef.current) return;
    const sections = scrollableRef.current.querySelectorAll('[data-role-section]');
    if (sections.length && typeof onSectionsRender === 'function') {
      onSectionsRender(sections);
    }
  }, [markdownContent, scrollableRef, onSectionsRender]);

  return (
    <Card className="px-lg-4 py-lg-2 border-0 role-markdown-panel d-flex flex-column bg-transparent shadow-none" style={{ boxShadow: 'none !important'}}>
      <CardBody className="p-0">
        <div ref={scrollableRef} className="role-markdown-body d-flex flex-column py-lg-2">
          <div className="role-markdown-renderer gap-5">
          {(isLoading || !role.id) && (
            <div className="d-flex justify-content-center align-items-center gap-2 text-secondary small">
              <Spinner className="text-primary-orange" />
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="d-grid gap-2">
              <CardText className="small text-secondary mb-0">{errorMessage}</CardText>
              <CardText className="small text-secondary mb-0">
                Expected file: <code>/role_descriptions/{roleDocFilename}</code>
              </CardText>
            </div>
          )}

          {!isLoading && !errorMessage && markdownContent && (
            <>
              {descriptionData.sections?.map(sectionData => (
                <div
                  data-role-section
                  id={sectionData.id}
                  key={`section-${sectionData.id}`}
                >
                  {((!isRoleCapped && role.isRegistrationOpen) || sectionData.id === 'overview') && (
                    <RoleDescriptionSectionCard
                      sectionData={sectionData}
                      roleId={role.id}
                      isRegistrationOpen={role.isRegistrationOpen}
                      isRoleCapped={isRoleCapped}
                      acceptedAcknowledgements={acceptedAcknowledgements}
                      submissionStatus={submissionStatus}
                      isApplyDisabled={isApplyDisabled}
                      scrollToSection={scrollToSection}
                      onApply={onApply}
                      toggleAcknowledgement={toggleAcknowledgement}
                      showConfirmModal={showConfirmModal}
                    />
                  )}
                </div>
              ))}
            </>
          )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export default RoleMarkdownRenderer;
